import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, Pencil, Trash2, GraduationCap, Loader2, Users } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export function AdminCapacitaciones({ token }) {
  const [capacitaciones, setCapacitaciones] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCap, setEditingCap] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', active: true });
  const [isSaving, setIsSaving] = useState(false);

  const loadCapacitaciones = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/capacitaciones`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCapacitaciones(data);
      }
    } catch (error) {
      console.error('Error loading capacitaciones:', error);
      toast.error('Error al cargar capacitaciones');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadCapacitaciones();
  }, [loadCapacitaciones]);

  const handleOpenCreate = () => {
    setEditingCap(null);
    setFormData({ name: '', description: '', active: true });
    setIsDialogOpen(true);
  };

  const handleOpenEdit = (cap) => {
    setEditingCap(cap);
    setFormData({
      name: cap.name,
      description: cap.description || '',
      active: cap.active
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }

    setIsSaving(true);
    try {
      const url = editingCap 
        ? `${API_BASE}/api/admin/capacitaciones/${editingCap._id}`
        : `${API_BASE}/api/admin/capacitaciones`;
      
      const res = await fetch(url, {
        method: editingCap ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success(editingCap ? 'Capacitación actualizada' : 'Capacitación creada');
        setIsDialogOpen(false);
        loadCapacitaciones();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Error al guardar');
      }
    } catch (error) {
      console.error('Error saving capacitacion:', error);
      toast.error('Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (cap) => {
    if (!window.confirm(`¿Eliminar "${cap.name}"? Esta acción no se puede deshacer.`)) {
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/api/admin/capacitaciones/${cap._id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Capacitación eliminada');
        loadCapacitaciones();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Error al eliminar');
      }
    } catch (error) {
      console.error('Error deleting capacitacion:', error);
      toast.error('Error al eliminar');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5" />
              Capacitaciones
            </CardTitle>
            <CardDescription>
              Gestiona las capacitaciones disponibles para los alumnos
            </CardDescription>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={handleOpenCreate} data-testid="add-capacitacion-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nueva Capacitación
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editingCap ? 'Editar Capacitación' : 'Nueva Capacitación'}
                </DialogTitle>
                <DialogDescription>
                  {editingCap 
                    ? 'Modifica los datos de la capacitación' 
                    : 'Agrega una nueva capacitación para tus alumnos'}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="cap-name">Nombre *</Label>
                  <Input
                    id="cap-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ej: Coaching Empresarial 2024"
                    data-testid="capacitacion-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cap-description">Descripción (opcional)</Label>
                  <Textarea
                    id="cap-description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe brevemente la capacitación..."
                    rows={3}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="cap-active">Activa</Label>
                    <p className="text-xs text-muted-foreground">
                      Las capacitaciones inactivas no aparecen en el formulario
                    </p>
                  </div>
                  <Switch
                    id="cap-active"
                    checked={formData.active}
                    onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleSave} disabled={isSaving} data-testid="save-capacitacion-btn">
                  {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {editingCap ? 'Guardar Cambios' : 'Crear'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {capacitaciones.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <GraduationCap className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No hay capacitaciones creadas</p>
            <p className="text-sm">Crea tu primera capacitación para empezar</p>
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-center">Formularios</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {capacitaciones.map((cap) => (
                  <TableRow key={cap._id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{cap.name}</p>
                        {cap.description && (
                          <p className="text-xs text-muted-foreground truncate max-w-[300px]">
                            {cap.description}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={cap.active ? 'default' : 'secondary'}>
                        {cap.active ? 'Activa' : 'Inactiva'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Users className="w-4 h-4 text-muted-foreground" />
                        <span>{cap.submission_count || 0}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenEdit(cap)}
                          data-testid={`edit-cap-${cap._id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(cap)}
                          className="text-destructive hover:text-destructive"
                          data-testid={`delete-cap-${cap._id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default AdminCapacitaciones;
